import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
	useAnimatedStyle,
	withSpring,
	withRepeat,
	withSequence,
	useSharedValue,
	useDerivedValue,
	type SharedValue,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { TextSkeleton } from "../ChatUI/TextSkeleton";
import { useAmplitudes } from "./useAmplitudes";
import type { AmplitudeMode } from "./useAmplitudes";

const BAR_WIDTH = 2;
const BAR_MARGIN = 1;
const MIN_HEIGHT = 2;
const MAX_HEIGHT = 25;

type WaveformBarProps = {
	amplitude: number;
	barWidth?: number;
	barMargin?: number;
	minHeight?: number;
	maxHeight?: number;
	activeColor?: string;
	inactiveColor?: string;
	isLoading?: boolean;
	index: number;
	activeBarIndex: SharedValue<number>;
};

const WaveformBar = memo(
	({
		amplitude,
		barWidth = BAR_WIDTH,
		barMargin = BAR_MARGIN,
		minHeight = MIN_HEIGHT,
		maxHeight = MAX_HEIGHT,
		activeColor,
		inactiveColor,
		isLoading = false,
		index,
		activeBarIndex,
	}: WaveformBarProps) => {
		const theme = useAppTheme();
		const heightShared = useSharedValue(0);
		const isActiveShared = useDerivedValue(() => index <= activeBarIndex.value);

		// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
		useEffect(() => {
			heightShared.value = minHeight + amplitude * (maxHeight - minHeight);
		}, [amplitude, minHeight, maxHeight]);

		const animatedStyle = useAnimatedStyle(() => {
			const targetHeight = heightShared.value;
			const isActive = isActiveShared.value;

			if (isLoading) {
				const rand1 = Math.random();
				const rand2 = Math.random();
				return {
					height: withRepeat(
						withSequence(
							withSpring(maxHeight * rand1, {
								damping: 10,
								stiffness: 40,
								mass: 0.5,
							}),
							withSpring(maxHeight * rand2, {
								damping: 10,
								stiffness: 40,
								mass: 0.5,
							}),
						),
						-1,
						true,
					),
					backgroundColor: theme.colors.surfaceDisabled,
					opacity: withSpring(0.4 + Math.random(), {
						damping: 15,
						stiffness: 40,
					}),
				};
			}

			return {
				height: withSpring(targetHeight, {
					damping: 20,
					stiffness: 220,
					mass: 0.3,
				}),
				backgroundColor: isActive
					? (activeColor ?? theme.palette.info.main)
					: (inactiveColor ?? theme.colors.surfaceDisabled),
				opacity: 1,
			};
		}, [activeColor, inactiveColor, isLoading]);

		const baseStyle = useMemo(
			() => ({
				width: barWidth,
				borderRadius: barWidth / 2,
				marginHorizontal: barMargin,
			}),
			[barWidth, barMargin],
		);

		return <Animated.View style={[baseStyle, animatedStyle]} />;
	},
);

export type WaveformVisualizerProps = {
	progress: number;
	/** Optional shared progress. When provided, the bar position is driven on the UI thread and React renders are skipped between play/pause events. */
	progressShared?: SharedValue<number>;
	recordingProgress?: number;
	uri?: string;
	barWidth?: number;
	barMargin?: number;
	minHeight?: number;
	maxHeight?: number;
	activeColor?: string;
	inactiveColor?: string;
	style?: React.ComponentProps<typeof Box>;
	meterHistory?: number[];
	mode?: AmplitudeMode;
	onSeek?: (progress: number) => Promise<void>;
	canSeek?: boolean;
	onAmplitudesLoading?: (isLoading: boolean) => void;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	cursor?: React.ComponentProps<typeof Box>["cursor"];
	showWaveform?: boolean;
	useReanimated?: boolean;
};

export const WaveformVisualizer = ({
	progress,
	progressShared,
	recordingProgress = 1,
	uri,
	barWidth = BAR_WIDTH,
	barMargin = BAR_MARGIN,
	minHeight = MIN_HEIGHT,
	maxHeight = MAX_HEIGHT,
	activeColor,
	inactiveColor,
	style,
	meterHistory = [],
	mode = "default",
	onSeek,
	canSeek = true,
	onAmplitudesLoading,
	waveformWidth,
	updateWaveformWidth,
	cursor = "grab",
	showWaveform = true,
	useReanimated = true,
}: WaveformVisualizerProps) => {
	const [containerWidth, setContainerWidth] = useState(waveformWidth || 0);
	const internalSeekProgress = useSharedValue(progress);
	const seekProgress = progressShared ?? internalSeekProgress;

	const barCount = useMemo(
		() => Math.floor(containerWidth / (barWidth + barMargin * 2)),
		[containerWidth, barWidth, barMargin],
	);

	useEffect(() => {
		if (updateWaveformWidth && waveformWidth === 0) {
			updateWaveformWidth(containerWidth);
		}
	}, [containerWidth, updateWaveformWidth, waveformWidth]);

	useEffect(() => {
		if (waveformWidth) {
			setContainerWidth(waveformWidth);
		}
	}, [waveformWidth]);

	const { amplitudes, isLoading: isAmplitudesLoading } = useAmplitudes({
		barCount,
		meterHistory,
		mode,
		uri,
		recordingProgress,
		onAmplitudesLoading,
	});

	const activeBarIndex = useDerivedValue(
		() => Math.floor(seekProgress.value * amplitudes.length),
		[amplitudes],
	);

	useEffect(() => {
		if (!progressShared) {
			internalSeekProgress.value = progress;
		}
	}, [progress, progressShared, internalSeekProgress]);

	const onLayout = useCallback((event: LayoutChangeEvent) => {
		setContainerWidth((prev) => {
			if (event.nativeEvent?.layout?.width > 0) {
				return event.nativeEvent.layout.width;
			}
			return prev;
		});
	}, []);

	const handleSeek = useCallback(
		async (newProgress: number) => {
			if (onSeek) {
				await onSeek(newProgress);
			}
		},
		[onSeek],
	);

	const panGesture = useMemo(
		() =>
			Gesture.Pan()
				.enabled(canSeek && showWaveform)
				.onUpdate((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / containerWidth));
					seekProgress.value = newProgress;
					scheduleOnRN(handleSeek, newProgress);
				}),
		[canSeek, showWaveform, containerWidth, handleSeek, seekProgress],
	);
	const tapGesture = useMemo(
		() =>
			Gesture.Tap()
				.enabled(canSeek && showWaveform)
				.onEnd((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / containerWidth));
					seekProgress.value = newProgress;
					scheduleOnRN(handleSeek, newProgress);
				}),
		[canSeek, showWaveform, containerWidth, handleSeek, seekProgress],
	);

	const gesture = useMemo(
		() => Gesture.Race(panGesture, tapGesture),
		[panGesture, tapGesture],
	);

	const theme = useAppTheme();

	return (
		<GestureDetector gesture={gesture}>
			<Box
				flexDirection="row"
				alignItems="center"
				height={maxHeight * 1.5}
				flex={1}
				overflow="hidden"
				onLayout={onLayout}
				cursor={cursor}
				{...style}
			>
				{showWaveform ? (
					containerWidth > 0 ? (
						amplitudes.map((amplitude, index) =>
							useReanimated ? (
								<WaveformBar
									isLoading={isAmplitudesLoading}
									key={index.toString()}
									amplitude={amplitude}
									index={index}
									activeBarIndex={activeBarIndex}
									barWidth={barWidth}
									barMargin={barMargin}
									minHeight={minHeight}
									maxHeight={maxHeight}
									activeColor={activeColor}
									inactiveColor={inactiveColor}
								/>
							) : (
								<Box
									key={index.toString()}
									height={minHeight + amplitude * (maxHeight - minHeight)}
									width={barWidth}
									backgroundColor={
										Math.floor(progress * amplitudes.length) > index
											? (activeColor ?? theme.palette.info.main)
											: (inactiveColor ?? theme.colors.surfaceDisabled)
									}
									marginHorizontal={barMargin}
								/>
							),
						)
					) : (
						<TextSkeleton height={16} />
					)
				) : null}
			</Box>
		</GestureDetector>
	);
};
